<template>
    <div class="h-full col-span-1 flex flex-col justify-between bg-white rounded-lg shadow">
        <div class="w-full flex-1 items-start p-4 relative">
            <div class="flex-1 mb-4">
                <div>
                    <!--Ticket Reference Number Badge (Top Right Corner Of Card)-->
                    <span class="absolute top-0 right-0 text-xs text-white font-medium rounded-tr-md rounded-bl-md rounded-br-0 px-2 uppercase"
                          :class="ticketBadgeBackground">
                        {{ mutableTask.ticket.reference_number }}
                    </span>

                    <!--Property Name / Back Button when viewing Map-->
                    <div class="mb-1 flex items-center align-middle text-gray-800 hover:text-indigo-600 w-10/12 cursor-pointer">
                        <h3 class="text-base leading-tight text-wrap font-medium"
                            @click="showAddressDetails =! showAddressDetails">
                            {{ showAddressDetails ? "<- Back" : mutableTask.asset.name }}
                        </h3>

                        <svg v-if="!showAddressDetails" class="w-4 h-4 ml-2 mb-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"
                             xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                  clip-rule="evenodd"></path>
                        </svg>
                    </div>
                </div>

                <!--Agency Name & Service Provider Task Status Dropdown-->
                <div v-if="!showAddressDetails" class="flex justify-between items-center">
                    <p class="text-gray-400 font-light text-sm leading-tight truncate">
                        {{ viewOnly ? mutableTask.service_provider_record.name : mutableTask.agency.name }}
                    </p>

                    <div v-if="!viewOnly" v-click-outside="hideUpdateStatusDropdown" @click="showUpdateStatusDropDown = !showUpdateStatusDropDown"
                         class="relative shadow-sm cursor-pointer select-none flex flex-shrink-0 justify-center items-center px-2 py-0.5 text-xs leading-4 font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:text-blue-900 rounded-full">
                        <span class="font-bold uppercase">{{ mutableTask.status }}</span>

                        <!-- Chevron/Arrow Down -->
                        <svg v-if="!showUpdateStatusDropDown" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                        <!-- Chevron/Arrow Left -->
                        <svg v-else class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>

                        <div v-show="showUpdateStatusDropDown"
                             class="absolute z-10 top-0 right-0 bg-gray-800 rounded-md p-3 mt-7 -mr-1">
                            <div class="absolute z-10 top-0 right-0 bg-gray-800 w-3 h-3 -mt-1 transform rotate-45 mr-5 rounded-tl-sm"></div>

                            <button v-for="status in statuses" @click.prevent="updateTaskStatus(status)"
                                    class="block whitespace-nowrap p-2 text-sm text-white font-medium w-min-content text-left align-middle outline-none hover:text-indigo-400 hover:bg-gray-700 rounded-md"
                                    :class="task.status === status ? 'text-indigo-400' : ''"
                            >{{ status.toUpperCase() }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="mutableTask.qa_required == true"
                 class="relative select-none flex flex-shrink-0 items-center p-1 pl-2 mb-2 text-sm leading-4 font-medium bg-orange-50 border border-orange-100 text-orange-700 rounded-md">
                <!-- Clock Icon For QA PENDING-->
                <svg v-if="mutableTask.qa_status === 'qa pending'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <!--- Checkmark Icon For QA COMPLETED-->
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                </svg>
                <span class="font-medium ml-2">{{ mutableTask.qa_status ? mutableTask.qa_status.toUpperCase() : "QA STATUS UNKNOWN" }}</span>
            </div>


            <!-- Address Details Section With Map-->
            <div v-if="showAddressDetails" class="mt-2 flex flex-col justify-between" :class="[showAddressDetails ? '' : 'h-full']">
                <iframe :src="mutableTask.asset.google_maps_url"
                        width="100%" height="260"
                        style="border:0"
                        allowfullscreen>
                </iframe>
                <h6 class="text-indigo-600 text-sm text-center font-medium mb-7 mt-4">{{ mutableTask.asset.full_address }}</h6>
            </div>

            <!-- Contact Details & task Last -->
            <div v-else>
                <task-card-contact-details v-if="showContactDetails" :task="mutableTask" :view-only="viewOnly" @send-qa-code="sendQACode"></task-card-contact-details>
                <!-- Task Item List -->
                <div v-else class="mt-2 select-none" key="task_items">
                    <div class="flex md:flex-row justify-between items-start">
                        <div class="w-full md:w-8/12">
                            <div class="flex items-center justify-start h-6" v-if="hasTaskItems">
                                <div class="font-medium mr-1">Task Items On</div>
                                <simple-dropdown :label="selectedDateGroup | datetime"
                                                 button-class="w-full text-gray-500 text-xs px-2 py-0.5 md:py-1 bg-white hover:bg-gray-100">
                                    <template slot="buttonOptions">
                                        <simple-dropdown-option v-for="datedGroup in Object.keys(mutableTask.task_items)"
                                                                :value="datedGroup"
                                                                :key="datedGroup + Math.random() * 99999"
                                                                :disable="generatingTaskCard"
                                                                :button-class="selectedDateGroup === datedGroup ? 'text-indigo-600' : ''"
                                                                @click="setVisibleItems(datedGroup)">
                                            {{ datedGroup | datetime }}
                                        </simple-dropdown-option>
                                    </template>
                                </simple-dropdown>
                            </div>

                            <div class="text-gray-400 text-xs md:whitespace-pre">Each time the task is updated, the selected task items will be grouped</div>
                        </div>

                        <!-- List of dates which task items are grouped at  -->
                        <div class="flex items-center justify-end w-1/12 md:w-3/12 pt-1">
                            <button @click.prevent="openTaskCard()"
                                    class="ml-1 outline-none flex items-center text-xs text-indigo-600 whitespace-nowrap hover:text-indigo-700 transition duration-300"
                                    :disabled="generatingTaskCard"
                                    v-if="!generatingTaskCard">
                                <icon-document-download class="w-4 h-4 mr-1 mb-0.5"
                                ></icon-document-download>
                                <template v-if="notSmallScreen">Download PDF</template>
                            </button>
                            <icon-circle-loading v-else class="animate-spin ml-1 w-5 h-5 text-indigo-600"></icon-circle-loading>
                        </div>
                    </div>

                    <div v-if="hasTaskItems" class="overflow-y-auto scrollbar-thin"
                         :style="viewOnly ? 'max-height: 600px' : 'max-height: 350px'">
                        <div v-for="items in visibleItems">
                            <!--TaskItems list-->
                            <ul v-if="items.length > 0" class="mt-3 text-sm">
                                <li v-for="item in items"
                                    class="my-2 px-2 py-1 flex items-center border border-indigo-100 bg-indigo-50 rounded-md">
                                    <div class="w-5" :class="[item.photos.length > 0 ? 'cursor-pointer text-indigo-600' : 'text-gray-400']">
                                        <!--Picture/Photos Icon-->
                                        <svg @click="viewTaskItemPhotos(item)"
                                             class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                        </svg>
                                    </div>

                                    <!--Action Required + Item Description-->
                                    <div class="flex-1 px-2 leading-tight text-xs text-indigo-600">
                                        <span class="rounded px-1 mb-2 text-indigo-600 font-extrabold leading-snug -ml-1">
                                            {{ item.required_action }}
                                        </span>
                                        <p class="mt-1">
                                            {{ item.description }}
                                        </p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div v-else>
                        <div class="py-8 w-full text-center font-lg text-gray-700">
                            No task items associated with this task
                        </div>
                    </div>

                    <!--Show more button -->
                    <span v-if="totalTaskItemsForGroup > 5"
                          class="text-indigo-600 mt-4 hover:text-indigo-800 text-xs font-bold flex justify-around cursor-pointer"
                          @click="toggleExpand">view {{ listExpanded ? "first 5 items" : totalTaskItemsForGroup - 5 + " more" }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Footer With Buttons -->
        <div v-if="!showAddressDetails">
            <!-- Assign users -->
            <div class="py-2 border-t border-gray-200 select-none" v-if="!showContactDetails  && assignableUsers && assignableUsers.length > 0">
                <div v-if="!mutableTask.users.length > 0" class="text-center py-1">
                    <div v-click-outside="hideAssignUserDropdown" @click.prevent="showAssignUserDropdown = !showAssignUserDropdown"
                         class="mx-auto relative items-center w-28 cursor-pointer text-white text-sm font-bold text-xs py-1 px-2 bg-red-500 rounded-lg hover:bg-red-800">
                        <div>UNASSIGNED</div>
                        <div v-show="showAssignUserDropdown"
                             class="z-10 py-3 w-36 bg-gray-800 rounded-md absolute top-9 right-0">
                            <div class="absolute z-10 top-0 right-0 bg-gray-800 w-3 h-3 -mt-1 transform rotate-45 mr-5 rounded-tl-sm"></div>
                            <button v-for="user in assignableUserList" :disabled="userAssignationLoading" @click.prevent="assignUser(user); assignedToCollapsed = false"
                                    class="w-full text-left block py-2 px-3 text-sm text-white font-medium hover:text-indigo-600 hover:bg-gray-700 rounded-md">
                                {{ user.name }}
                            </button>
                        </div>
                    </div>
                    <span class="text-xs text-gray-400">Click to assign a chosen team member to this task for tracking</span>
                </div>

                <div v-else>
                    <div v-if="!assignedToCollapsed" class="flex-col items-center text-center px-3 py-1 -mt-1">
                        <div class=" text-sm text-gray-500 flex justify-center items-center mb-4">

                            <button class="hover:text-indigo-700 outline-none" @click="assignedToCollapsed = true">Assigned</button>
                            <div class="whitespace-pre"> to</div>

                            <button v-click-outside="hideAssignUserDropdown" @click.prevent="showAssignUserDropdown = !showAssignUserDropdown"
                                    v-if="assignableUserList.length > 0"
                                    class="relative outline-none text-gray-400 py-1">
                                <icon-user-plus class="ml-2 w-4 h-4"></icon-user-plus>
                                <!-- Icon Button to assign more users -->
                                <div v-show="showAssignUserDropdown"
                                     class="z-10 py-3 w-36 bg-gray-800 rounded-md absolute top-9 right-0">
                                    <div class="absolute z-10 right-1 top-0 bg-gray-800 w-3 h-3 -mt-1 transform rotate-45 rounded-tl-sm"></div>
                                    <button v-for="user in assignableUserList" :disabled="userAssignationLoading" @click.prevent="assignUser(user)"
                                            class="w-full text-left block py-2 px-3 text-sm text-white font-medium hover:text-indigo-600 hover:bg-gray-700 rounded-md">
                                        {{ user.name }}
                                    </button>
                                </div>
                            </button>
                        </div>

                        <!-- Only add margin-bottom when there are multiple users to keep the footer size the same as an unassigned task footer -->
                        <div v-for="user in mutableTask.users" class="px-2 flex justify-between max-w-min text-gray-400 hover:bg-gray-100 rounded-lg"
                             :class="mutableTask.users.length > 1 ? 'mb-2' : ''">
                            <div class="text-sm">{{ user.name }}</div>
                            <button @click.prevent="removeUser(user)" :disabled="userAssignationLoading" class="outline-none">
                                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div v-else>
                        <div class="flex justify-center text-xs">
                            <!-- Show assigned to footer field-->
                            <button @click.prevent="assignedToCollapsed = false"
                                    class="flex items-center outline-none text-gray-400 hover:text-indigo-700">
                                <div class="mr-1">Assigned</div>
                                <icon-chevron-down-double class="w-3 h-3" aria-hidden="true"></icon-chevron-down-double>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="-mt-px flex border-t border-gray-200">
                <!--Messenger Channel Button-->
                <div class="w-0 flex-1 flex border-r border-gray-200 hover:shadow-md">
                    <button v-if="viewOnly" @click.prevent="$emit('close-task')"
                            class="relative -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-indigo-600 focus:outline-none focus:ring focus:ring-blue-400 focus:border-blue-300 focus:z-10 transition ease-in-out duration-150">
                        <div class="text-gray-400">
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        <span class="ml-3">Close</span>
                    </button>

                    <button v-else @click.prevent="openChannel"
                            class="relative outline-none -mr-px w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-bl-lg hover:text-indigo-600 focus:outline-none focus:ring focus:ring-blue-400 focus:border-blue-300 focus:z-10 transition ease-in-out duration-150">
                        <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path
                                stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                        <span class="ml-3">{{ fetchingChannelDetail ? "Loading ..." : "Messenger" }}</span>
                    </button>
                </div>

                <!--Show Contact Details Button-->
                <div class="-ml-px w-0 flex-1 flex hover:shadow-md">
                    <a href="#" @click.prevent="showContactDetails = !showContactDetails"
                       class="relative w-0 flex-1 inline-flex items-center justify-center py-4 text-sm leading-5 text-gray-700 font-medium border border-transparent rounded-br-lg hover:text-indigo-600 focus:outline-none focus:ring focus:ring-blue-400 focus:border-blue-300 focus:z-10 transition ease-in-out duration-150">
                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path v-if="showContactDetails" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01">
                            </path>
                            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
                            </path>
                        </svg>

                        <span v-if="showContactDetails" class="ml-3">View Items</span>
                        <span v-else class="ml-3">Contact Details</span>
                    </a>
                </div>
            </div>
        </div>

        <!-- Quality Assurance confirmation code        -->
        <confirmation-dialog v-if="showQACodeDialog"
                             :show="showQACodeDialog"
                             title="Insert QA Code"
                             subtitle="Please provide the code that was sent to the contact you selected."
                             proceed-button-text="Confirm QA Code"
                             @proceed="confirmQA"
                             @cancelled="cancelQAConfirmation">
            <template slot="icon">
                <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                    <!-- Badge Icon -->
                    <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                    </svg>
                </div>
            </template>

            <template slot="body">
                <text-input
                    input-label=""
                    input-class-list="form-input block w-full text-2xl text-center"
                    v-model="qaConfirmationForm.qa_code"
                    :has-errors="qaConfirmationForm.errors.has('qa_code')">
                    <template slot="errorMessage">
                        <p v-if="qaConfirmationForm.errors.has('qa_code')"
                           class="mt-1 text-lg text-red-800 text-center w-full leading-tight">{{ qaConfirmationForm.errors.get("qa_code") }}
                        </p>
                    </template>
                </text-input>
            </template>
        </confirmation-dialog>

        <!--Photo Viewer for Items with photos-->
        <lightbox v-if="photoLightBoxVisible" :show-lightbox="photoLightBoxVisible" :images="imageUrls" @close="photoLightBoxVisible = false" :width="400"
                  :height="400"></lightbox>
    </div>
</template>

<script>
export default {
    name: "TaskCard",
    props: {
        task: {
            type: Object,
            required: true
        },
        viewOnly: {
            type: Boolean,
            required: true,
            description: "The task card is view only when an agency team views it"
        },
        assignableUsers: {
            type: Array,
            required: false,
            default: null,
            description: "The users which can be assigned to the task"
        },
        statuses: {
            type: Array,
            required: false,
            description: "Task statuses used for filtering",
        }
    },
    data () {
        return {
            channel: null,
            fetchingChannelDetail: false,
            mutableTask: this.task,
            totalTaskItemsForGroup: 0,
            listExpanded: false,
            photoLightBoxVisible: false,
            selectedTaskItem: null,
            showContactDetails: false,
            showUpdateStatusDropDown: false,
            showAddressDetails: false,
            visibleItems: [],
            selectedDateGroup: null,
            showQACodeDialog: false,
            qaRecord: null,
            qaConfirmationForm: new AppForm({
                qa_code: null
            }),
            assignableUserList: this.assignableUsers,
            showAssignUserDropdown: false,
            assignedToCollapsed: true,
            userAssignationLoading: false,
            generatingTaskCard: false,
        };
    },
    mounted () {
        this.setVisibleItems();
        if (this.assignableUsers) {
            let self = this;
            this.assignableUserList = this.assignableUsers.filter(function (user) {
                return !self.mutableTask.users.find(function (u) {
                    return user.id === u.id;
                });
            });
        }
    },
    watch: {
        "mutableTask.users.length": function () {
            let self = this;
            this.assignableUserList = this.assignableUsers.filter(function (user) {
                return !self.mutableTask.users.find(function (u) {
                    return user.id === u.id;
                });
            });
        }
    },
    computed: {
        ticketBadgeBackground () {
            switch (this.mutableTask.ticket.priority) {
                case "low":
                    return "bg-green-500";
                case "medium":
                    return "bg-indigo-500";
                case "high":
                    return "bg-red-500";
            }
        },

        imageUrls () {
            if (this.selectedTaskItem == null) {
                return;
            }
            return this.selectedTaskItem.photos.map(file => file.url);
        },

        hasTaskItems () {
            return Object.keys(this.mutableTask.task_items).length > 0;
        },
    },
    methods: {
        sendQACode (recipient) {
            App.post("/sp/tasks/" + this.mutableTask.id + "/send-qa-code", new AppForm({
                recipient: recipient
            })).then((response) => {
                this.qaRecord = response.qaRecord;
                this.showQACodeDialog = true;
                toastr.success(response.message);
            }).catch((errors) => {
                toastr.error(errors.message);
            });
        },

        confirmQA () {
            App.post(`/sp/tasks/${this.mutableTask.id}/qarecords/${this.qaRecord.id}/complete`, this.qaConfirmationForm)
            .then(response => {
                toastr.success("Quality Assurance Successfully Completed");
                this.mutableTask.qa_status = response.message;
                this.showQACodeDialog = false;
                this.qaRecord = null;
                this.qa_code = null;
            }).catch(error => {
                toastr.error(response.message);
            });
        },

        cancelQAConfirmation () {
            this.showQACodeDialog = false;
        },

        toggleExpand () {
            this.listExpanded = !this.listExpanded;
            this.setVisibleItems();
        },

        viewTaskItemPhotos (taskItem) {
            if (taskItem.photos.length > 0) {
                this.selectedTaskItem = taskItem;
                this.photoLightBoxVisible = true;
            }
        },

        setVisibleItems (datedGroup = null) {
            // Here we split the mutableTask.task_items object into its grouped dates and
            // select the items in the select group, while the default is always the latest group.
            // Then we split the items into 5 if there are 5 or more, until the list is expanded
            if (this.mutableTask.task_items && this.mutableTask.task_items.length === 0) {
                this.visibleItems = [];
                return;
            }

            let taskItemsKeys = Object.keys(this.mutableTask.task_items);
            let taskItemsValues = Object.values(this.mutableTask.task_items);

            this.selectedDateGroup = datedGroup ? datedGroup : (this.selectedDateGroup ? this.selectedDateGroup : taskItemsKeys[taskItemsKeys.length - 1]);
            let index = taskItemsKeys.indexOf(this.selectedDateGroup);
            this.totalTaskItemsForGroup = Object.values(Object.values(this.mutableTask.task_items)[index]).length;

            if (index !== -1) {
                if (this.listExpanded) {
                    this.visibleItems = _.groupBy(taskItemsValues[index], "created_at");
                } else {
                    this.visibleItems = _.groupBy(taskItemsValues[index].slice(0, 5), "created_at");
                }
            }
        },

        hideUpdateStatusDropdown () {
            this.showUpdateStatusDropDown = false;
        },

        updateTaskStatus (status) {
            App.patch("/sp/tasks/" + this.mutableTask.id, new AppForm({
                status: _.lowerCase(status)
            })).then(() => {
                this.mutableTask.status = status;
                this.hideUpdateStatusDropdown();
                this.$emit("status-changed");
                toastr.success("Task Status Updated");
            }).catch((errors) => {
                this.hideUpdateStatusDropdown();
                toastr.error("Could not update task status! " + errors.message);
            });
        },

        openChannel () {
            this.fetchingChannelDetail = true;
            axios.get("/sp/messenger/channels/task/" + this.task.id, {})
            .then((response) => {
                this.channel = response.data;
                this.$emit("open-redrabbit-messenger", response.data);
                this.fetchingChannelDetail = false;
            }).catch((errors) => {
                toastr.error("Could not load RedRabbit Messenger for task! " + errors.message);
                this.fetchingChannelDetail = false;
            });
        },

        hideAssignUserDropdown () {
            this.showAssignUserDropdown = false;
        },

        assignUser (user) {
            this.userAssignationLoading = true;
            if (user.id) {
                if (this.mutableTask.users.findIndex(u => u.id === user.id) > -1) {
                    toastr.error("That user is already assigned to this task");
                    return;
                }

                axios.post("/sp/tasks/" + this.task.id + "/user/assign", new AppForm({
                    user_id: user.id,
                })).then((response) => {
                    this.mutableTask.users.push(user);
                    this.userAssignationLoading = false;
                    toastr.success(response.data);
                }).catch((error) => {
                    this.userAssignationLoading = false;
                    toastr.error("Could not assign user to task. Error: " + error.message);
                });
            }
        },

        removeUser (user) {
            this.userAssignationLoading = true;
            if (user.id) {
                axios.delete("/sp/tasks/" + this.task.id + "/user/unassign", {
                    data: {
                        user_id: user.id
                    }
                }).then((response) => {
                    // find and remove user from task's user array
                    let index = this.mutableTask.users.findIndex(u => u.id === user.id);
                    this.mutableTask.users.splice(index, 1);
                    this.userAssignationLoading = false;
                    toastr.success(response.data);
                }).catch((error) => {
                    this.userAssignationLoading = false;
                    toastr.error("Could not remove user from task. Error: " + error.message);
                });
            }
        },

        openTaskCard () {
            this.generatingTaskCard = true;

            let index = Object.keys(this.mutableTask.task_items).indexOf(this.selectedDateGroup);
            let url = this.viewOnly ? `/tasks/${this.mutableTask.id}/generate-task-card` : `/sp/tasks/${this.mutableTask.id}/generate-task-card`;

            App.post(url, new AppForm({
                taskItems: Object.values(this.mutableTask.task_items)[index]
            })).then((response) => {
                window.open(response.url, "_blank");
                this.generatingTaskCard = false;
            }).catch((error) => {
                this.generatingTaskCard = false;
                toastr.error("Could not generate Task Card PDF. Error: " + error.message);
            });
        },
    }
};
</script>
